// src/views/NearbyPlayers.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Alert,
  Button,
  Card,
  Container,
  Form,
  InputGroup,
  Spinner,
} from 'react-bootstrap';
import AppToolbar from '../components/AppToolbar';
import MessagesModal from '../components/MessagesModal';
import SettingsModal from './SettingsModal';
import AuthDialog from '../components/AuthDialog';
import { useAuth } from '../contexts/AuthContext';
import { firestore } from '../views/firebaseConfig';
import {
  collection,
  deleteField,
  doc,
  GeoPoint,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import PlayerProfileModal from '../components/PlayerProfileModal';
import searchAsset from '../assets/android/ui/p4r_feed_search.svg';
import './NearbyPlayers.css';

const SEARCH_LIMIT_WORDS = 4;
const NEARBY_RADIUS_MILES = 14;

const demoPlayers = [
  {
    id: 'demo-1',
    nickname: 'Manny',
    homeBase: 'San Juan, PR',
    onlineStatus: 'online',
    profileImageUrl: 'https://placehold.co/160x160/101010/FFD45C?text=P4R',
  },
  {
    id: 'demo-2',
    nickname: 'Sofia',
    homeBase: 'Miami, FL',
    onlineStatus: 'offline',
    profileImageUrl: 'https://placehold.co/160x160/161616/FFD45C?text=8',
  },
  {
    id: 'demo-3',
    nickname: 'Chris',
    homeBase: 'Houston, TX',
    onlineStatus: 'online',
    profileImageUrl: 'https://placehold.co/160x160/1d1d1d/FFD45C?text=9',
  },
];

function cleanWords(value) {
  return value
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean)
    .slice(0, SEARCH_LIMIT_WORDS);
}

function milesBetween(a, b) {
  if (!a || !b) return null;

  const latA = Number(a.latitude);
  const lonA = Number(a.longitude);
  const latB = Number(b.latitude);
  const lonB = Number(b.longitude);

  if ([latA, lonA, latB, lonB].some((value) => Number.isNaN(value))) return null;

  const dLat = Math.abs(latB - latA) * 69;
  const dLon = Math.abs(lonB - lonA) * (69 * Math.cos((latA * Math.PI) / 180));
  return Math.hypot(dLat, dLon);
}

function getLocationPoint(profile) {
  if (!profile?.location) return null;
  return {
    latitude: profile.location.latitude,
    longitude: profile.location.longitude,
  };
}

function PlayerCard({ player, locked = false, onClick }) {
  const status = player.onlineStatus === 'online' ? 'online' : 'offline';
  const displayName = player.nickname || player.name || 'Pool player';
  const imageUrl = player.profileImageUrl || 'https://placehold.co/160x160/101010/FFD45C?text=P4R';
  const strengths = Array.isArray(player.keyStrengths) ? player.keyStrengths.slice(0, 2) : [];
  const rating = player.fargoRating || player.otherRating || '';
  const isCoach = player.coachStatus === 'on';

  return (
    <Card
      className={`p4r-nearby-card ${locked ? 'p4r-nearby-card-locked' : ''}`}
      onClick={locked ? undefined : onClick}
      role={locked ? undefined : 'button'}
      tabIndex={locked ? -1 : 0}
      onKeyDown={(event) => {
        if (!locked && (event.key === 'Enter' || event.key === ' ')) onClick?.();
      }}
    >
      <Card.Body>
        <div className="p4r-nearby-avatar-wrap">
          <img src={imageUrl} alt={displayName} className="p4r-nearby-avatar" />
          <span className={`p4r-nearby-status-dot ${status}`} aria-label={status} />
        </div>

        <div className="p4r-nearby-main">
          <div className="p4r-nearby-name-row">
            <strong>{displayName}</strong>
            {player.distanceMiles != null && <span>{player.distanceMiles.toFixed(1)} mi</span>}
          </div>
          <div className="p4r-nearby-meta-row">
            <span>{isCoach ? 'Coach' : player.homeBase || player.city || 'Pool 4 Real'}</span>
            <span>{rating ? `Skill ${rating}` : status}</span>
          </div>
          {(strengths.length > 0 || player.cueImageUrl) && (
            <div className="p4r-nearby-player-tags">
              {strengths.map((strength) => <span key={strength}>{strength}</span>)}
              {player.cueImageUrl && <span>Cue media</span>}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

export default function NearbyPlayers() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState('');
  const [players, setPlayers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [isOnline, setIsOnline] = useState(false);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showMessagesModal, setShowMessagesModal] = useState(false);

  const canonicalUrl = `${window.location.origin}${location.pathname}${location.search}`;
  const hasSearch = searchQuery.trim().length > 0;

  useEffect(() => {
    if (authLoading || !user) return undefined;

    let mounted = true;
    getDoc(doc(firestore, 'userprofiles', user.uid))
      .then((snap) => {
        if (!mounted) return;
        setIsOnline(snap.exists() && snap.data().onlineStatus === 'online');
      })
      .catch(() => {
        if (mounted) setIsOnline(false);
      });

    return () => {
      mounted = false;
    };
  }, [authLoading, user]);

  const fetchNearby = useCallback(async () => {
    if (!user) return;

    setIsSearching(true);
    setLocationError('');

    try {
      const meSnap = await getDoc(doc(firestore, 'userprofiles', user.uid));
      const myLocation = getLocationPoint(meSnap.exists() ? meSnap.data() : null);

      if (!myLocation) {
        setPlayers([]);
        setLocationError('Turn on player visibility to find nearby players.');
        return;
      }

      const allSnap = await getDocs(collection(firestore, 'userprofiles'));
      const nearby = allSnap.docs
        .map((snap) => ({ id: snap.id, ...snap.data() }))
        .filter((player) => player.id !== user.uid)
        .map((player) => ({
          ...player,
          distanceMiles: milesBetween(myLocation, getLocationPoint(player)),
        }))
        .filter((player) => player.distanceMiles != null && player.distanceMiles <= NEARBY_RADIUS_MILES)
        .filter((player) => !onlineOnly || player.onlineStatus === 'online')
        .sort((a, b) => (a.distanceMiles ?? 999) - (b.distanceMiles ?? 999));

      setPlayers(nearby);
    } catch (error) {
      console.error('Nearby players failed:', error);
      setPlayers([]);
      setLocationError('Unable to load players right now.');
    } finally {
      setIsSearching(false);
    }
  }, [onlineOnly, user]);

  const fetchSearch = useCallback(async () => {
    if (!user) return;

    const words = cleanWords(searchQuery);
    if (!words.length) {
      fetchNearby();
      return;
    }

    setIsSearching(true);
    setLocationError('');

    try {
      const map = new Map();

      await Promise.all(
        words.map(async (word) => {
          const snap = await getDocs(
            query(
              collection(firestore, 'userprofiles'),
              where('nickname_words', 'array-contains', word)
            )
          );

          snap.docs.forEach((item) => {
            const player = { id: item.id, ...item.data() };
            if (player.id !== user.uid && (!onlineOnly || player.onlineStatus === 'online')) {
              map.set(player.id, player);
            }
          });
        })
      );

      setPlayers(Array.from(map.values()));
    } catch (error) {
      console.error('Player search failed:', error);
      setPlayers([]);
      setLocationError('Search failed. Try again.');
    } finally {
      setIsSearching(false);
    }
  }, [fetchNearby, onlineOnly, searchQuery, user]);

  const refreshPlayers = useCallback(() => {
    if (hasSearch) fetchSearch();
    else fetchNearby();
  }, [fetchNearby, fetchSearch, hasSearch]);

  useEffect(() => {
    if (authLoading || !user) return;
    const id = setTimeout(refreshPlayers, hasSearch ? 260 : 0);
    return () => clearTimeout(id);
  }, [authLoading, hasSearch, onlineOnly, refreshPlayers, searchQuery, user]);

  const toggleMyVisibility = async () => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    const profileRef = doc(firestore, 'userprofiles', user.uid);
    const goingOnline = !isOnline;
    setLocationError('');

    if (!goingOnline) {
      setIsOnline(false);
      await updateDoc(profileRef, {
        onlineStatus: 'offline',
        location: deleteField(),
      });
      refreshPlayers();
      return;
    }

    if (!navigator.geolocation) {
      setLocationError('Location is not available on this device.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        await updateDoc(profileRef, {
          onlineStatus: 'online',
          location: new GeoPoint(position.coords.latitude, position.coords.longitude),
        });
        setIsOnline(true);
        refreshPlayers();
      },
      () => setLocationError('Location permission is required to appear nearby.'),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  };

  const lockedPreview = useMemo(() => demoPlayers, []);

  if (authLoading) return null;

  return (
    <>
      <Helmet>
        <title>{hasSearch ? `Search Players | Pool 4 Real` : 'Nearby Players | Pool 4 Real'}</title>
        <meta name="description" content="Find and connect with pool players on Pool 4 Real." />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <AppToolbar
        currentPage="players"
        onNavigate={(view) => setActiveModal(view)}
        onSearchToggle={() => setFiltersOpen((value) => !value)}
        searchOpen={filtersOpen}
        onOpenMessages={() => (user ? setShowMessagesModal(true) : setShowLoginModal(true))}
      />

      {activeModal === 'settings' && <SettingsModal show onClose={() => setActiveModal(null)} />}
      <MessagesModal show={showMessagesModal} onClose={() => setShowMessagesModal(false)} />

      <AuthDialog
        show={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        subtitle="Sign in to appear nearby, open player profiles, and start conversations."
      />

      <Container fluid className="p4r-nearby-shell">
        <section className="p4r-nearby-topbar">
          <div>
            <p className="p4r-nearby-kicker">Players</p>
            <h1>Nearby</h1>
          </div>

          <div className="p4r-nearby-actions">
            <button
              type="button"
              className={`p4r-nearby-icon-btn ${filtersOpen ? 'active' : ''}`}
              onClick={() => setFiltersOpen((value) => !value)}
              aria-label="Search and filters"
            >
              <img src={searchAsset} alt="" className="p4r-nearby-search-asset" />
            </button>
            <button
              type="button"
              className={`p4r-nearby-presence ${isOnline ? 'online' : ''}`}
              onClick={toggleMyVisibility}
            >
              <span />
              {isOnline ? 'Visible' : 'Hidden'}
            </button>
          </div>
        </section>

        {user && filtersOpen && (
          <section className="p4r-nearby-filter-panel">
            <InputGroup className="p4r-nearby-search-group">
              <Form.Control
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search players"
                aria-label="Search players"
              />
              {searchQuery && (
                <Button variant="dark" onClick={() => setSearchQuery('')} aria-label="Clear search">
                  ×
                </Button>
              )}
              <Button variant="dark" onClick={refreshPlayers} disabled={isSearching}>
                {isSearching ? <Spinner animation="border" size="sm" /> : 'Go'}
              </Button>
            </InputGroup>

            <div className="p4r-nearby-chip-row">
              <button
                type="button"
                className={!onlineOnly ? 'active' : ''}
                onClick={() => setOnlineOnly(false)}
              >
                All
              </button>
              <button
                type="button"
                className={onlineOnly ? 'active' : ''}
                onClick={() => setOnlineOnly(true)}
              >
                Online
              </button>
            </div>
          </section>
        )}

        {locationError && user && <Alert className="p4r-nearby-alert">{locationError}</Alert>}

        {!user ? (
          <section className="p4r-nearby-guest-lock">
            <div className="p4r-nearby-list locked">
              {lockedPreview.map((player) => (
                <PlayerCard player={player} locked key={player.id} />
              ))}
            </div>
            <div className="p4r-nearby-lock-panel">
              <strong>Sign in to view players</strong>
              <button type="button" onClick={() => setShowLoginModal(true)}>
                Continue
              </button>
            </div>
          </section>
        ) : (
          <section className="p4r-nearby-content">
            {isSearching && (
              <div className="p4r-nearby-loading">
                <Spinner animation="border" />
              </div>
            )}

            {!isSearching && players.length === 0 && (
              <div className="p4r-nearby-empty">
                <strong>No players found</strong>
                <button type="button" onClick={toggleMyVisibility}>
                  {isOnline ? 'Refresh nearby' : 'Become visible'}
                </button>
              </div>
            )}

            {!isSearching && players.length > 0 && (
              <div className="p4r-nearby-list">
                {players.map((player) => (
                  <PlayerCard
                    key={player.id}
                    player={player}
                    onClick={() => setSelectedPlayer(player)}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        <PlayerProfileModal
          show={!!selectedPlayer}
          player={selectedPlayer}
          onHide={() => setSelectedPlayer(null)}
        />
      </Container>
    </>
  );
}
